import { MutableRefObject, useCallback, useRef, useState } from "react";
import constate from "constate";
import shortid from "shortid";

export const BREADCRUMB_TYPES = ["Default", "Acccount"];
type BreadcrumbType = typeof BREADCRUMB_TYPES[number];

export interface Breadcrumb {
  label?: string;
  to?: string;
  type?: BreadcrumbType;
}

type CrumbId = string;
type CrumbUpdater = (crumb: Partial<Breadcrumb>) => void;
type CrumbRemover = () => void;

export type BreadcrumbItem = Breadcrumb & { id: CrumbId };

export type CrumbInstance = {
  id: CrumbId;
  update: CrumbUpdater;
  remove: CrumbRemover;
};

export const useBreadcrumbInstance = () => {
  const [crumbs, setCrumbs] = useState<BreadcrumbItem[]>([]);
  const crumbsRef = useRef(crumbs);
  crumbsRef.current = crumbs; // sync with render state

  // Appends a crumb to the end of the array
  const addCrumb = useCallback((crumb: Breadcrumb): CrumbId => {
    const id: CrumbId = shortid.generate();
    const next = [...crumbsRef.current, { id, ...crumb }];
    crumbsRef.current = next;

    setCrumbs(crumbsRef.current);

    return id;
  }, []);

  const updateCrumb = useCallback(
    (id: CrumbId) => (crumb: Partial<Breadcrumb>) => {
      const crumbs = crumbsRef.current;
      const crumbIdx = crumbs.findIndex((c) => c.id === id);

      // manage update
      if (crumbIdx >= 0) {
        const next = crumbs.map((existing, index) =>
          index === crumbIdx
            ? {
                ...existing,
                ...crumb,
              }
            : existing,
        );

        crumbsRef.current = next;
        setCrumbs(crumbsRef.current);
      }
    },
    [],
  );

  // Removes a crumb from the array by its id
  // Note: if we were to modify `crumbs` directly inside this function, we would
  //       cause an infinite loop as the function is recalculated on every change
  const removeCrumb = useCallback(
    (id: CrumbId) => () => {
      const crumbs = crumbsRef.current;
      const next = crumbs.filter((c) => c.id !== id);

      if (next.length !== crumbs.length) {
        crumbsRef.current = next; // update our internal reference to the crumbs
        setCrumbs(crumbsRef.current); // set the rendered crumbs to the internally up-to-date version
      }
    },
    [],
  );

  const register = useCallback(
    (crumb: Partial<Breadcrumb>) => (
      ref: MutableRefObject<CrumbInstance | undefined>,
    ) => {
      const id: CrumbId = addCrumb(crumb);

      const updater = updateCrumb(id);
      const remover = removeCrumb(id);

      // Assign the instance of the registered crumb to the ref that was passed in
      // This is the way the caller `useCrumb` will manage the interaction of utils
      ref.current = {
        id,
        update: updater,
        remove: remover,
      };
    },
    [addCrumb, removeCrumb, updateCrumb],
  );

  return {
    crumbs,
    register,
  };
};

export const [
  BreadcrumbsProvider,
  useBreadcrumbs,
  useBreadcrumbUtils,
] = constate(
  useBreadcrumbInstance,
  ({ crumbs }) => crumbs,
  ({ register }) => ({ register }),
);
